<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="lg" v-if="!isLoading">
        <v-card-title class="mt-2">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          <span>Ticket #{{ ticket.id }}</span>
          <v-chip
            small
            :color="getStatusColor(ticket.status)"
            text-color="white"
            class="font-weight-medium ml-3"
          >
            <v-icon left small>{{ getStatusIcon(ticket.status) }}</v-icon>
            {{ getStatusLabel(ticket.status) }}
          </v-chip>
        </v-card-title>

        <v-card-text>
          <v-row>
            <!-- Ticket details -->
            <v-col cols="12" md="6">
              <div class="text-overline grey--text">Title</div>
              <div class="text-body-1 font-weight-medium mb-3">
                {{ ticket.title || "(No title)" }}
              </div>

              <div class="text-overline grey--text">Complaint Description</div>
              <div class="text-body-2 mb-3" style="white-space: pre-line">
                {{ ticket.description || "-" }}
              </div>

              <div class="text-overline grey--text">Raised By</div>
              <div class="text-body-2 mb-3">
                {{ getUserName(ticket.created_by) }}
                <span class="grey--text text-caption"
                  >on {{ formatDate(ticket.created_at) }}</span
                >
              </div>

              <div class="text-overline grey--text">Members</div>
              <div class="mb-3">
                <v-chip
                  v-for="member in ticket.users"
                  :key="member.id"
                  small
                  outlined
                  color="primary"
                  class="mr-1 mb-1"
                >
                  <v-icon left x-small>mdi-account</v-icon>
                  {{ getUserName(member) }}
                </v-chip>
                <span v-if="!ticket.users || !ticket.users.length" class="text-caption grey--text">
                  No members
                </span>
              </div>
            </v-col>

            <!-- Status update -->
            <v-col cols="12" md="6">
              <div class="text-overline grey--text">Update Status</div>
              <v-select
                v-model="selectedStatus"
                :items="statuses"
                item-text="text"
                item-value="value"
                outlined
                dense
                hide-details
                class="mb-3"
                :disabled="!canUpdate('raise_tickets')"
              ></v-select>
              <v-btn
                color="primary"
                small
                :loading="isUpdatingStatus"
                :disabled="selectedStatus === ticket.status || !canUpdate('raise_tickets')"
                @click="updateStatus"
              >
                Save Status
              </v-btn>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <!-- Message thread -->
          <div class="text-overline grey--text mb-2">Conversation</div>

          <div ref="messagesContainer" class="rt-msgs">
            <div v-if="isLoadingMessages" class="text-center py-6">
              <v-progress-circular indeterminate color="primary" size="30"></v-progress-circular>
            </div>

            <div v-else-if="!messages.length" class="text-center py-6 grey--text">
              No messages yet
            </div>

            <template v-else>
              <div
                v-for="message in messages"
                :key="message.id"
                class="rt-msg-row"
                :class="{ 'rt-msg-row--mine': isMine(message) }"
              >
                <div class="rt-msg-bubble" :class="{ 'rt-msg-bubble--mine': isMine(message) }">
                  <div class="rt-msg-sender">{{ getUserName(message.sender) }}</div>
                  <div v-if="message.message" class="rt-msg-text">{{ message.message }}</div>
                  <a
                    v-if="message.has_file"
                    :href="message.file_url"
                    target="_blank"
                    class="rt-msg-file"
                  >
                    <v-icon small left>mdi-paperclip</v-icon>
                    Attachment
                  </a>
                  <div class="rt-msg-time">{{ formatTime(message.created_at) }}</div>
                </div>
              </div>
            </template>
          </div>

          <div class="d-flex align-center mt-3">
            <v-btn icon small class="mr-1" @click="$refs.fileInput.click()">
              <v-icon>mdi-paperclip</v-icon>
            </v-btn>
            <input
              ref="fileInput"
              type="file"
              class="d-none"
              @change="handleFileSelect"
            />
            <v-text-field
              v-model="newMessage"
              placeholder="Type a reply..."
              outlined
              dense
              hide-details
              @keydown.enter="sendMessage"
            ></v-text-field>
            <v-btn
              icon
              color="primary"
              class="ml-1"
              :loading="isSending"
              :disabled="!newMessage.trim() && !selectedFile"
              @click="sendMessage"
            >
              <v-icon>mdi-send</v-icon>
            </v-btn>
          </div>
          <div v-if="selectedFile" class="text-caption grey--text mt-1">
            <v-icon x-small>mdi-file</v-icon> {{ selectedFile.name }}
            <v-btn icon x-small @click="selectedFile = null">
              <v-icon x-small>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-text>
      </v-card>

      <!-- Loading State -->
      <v-card elevation="6" rounded="lg" v-else>
        <v-card-text class="text-center" style="height: 400px">
          <health-loader message="Loading ticket..." />
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "RaiseTicketIdPage",
  data() {
    return {
      ticket: {},
      messages: [],
      isLoading: true,
      isLoadingMessages: true,
      isUpdatingStatus: false,
      isSending: false,
      selectedStatus: null,
      newMessage: "",
      selectedFile: null,
      statuses: [
        { text: "Pending", value: 0 },
        { text: "In Process", value: 1 },
        { text: "Completed", value: 2 },
        { text: "Rejected", value: 3 },
      ],
    };
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      try {
        this.isLoading = true;
        const encryptedId = this.$route.params.id;
        const response = await axios.get(`/raise_tickets/${encryptedId}`);

        if (response.data.success) {
          this.ticket = response.data.data;
          this.selectedStatus = this.ticket.status;
          this.getMessages();
        } else {
          this.$store.dispatch("errors/setError", {
            message: response.data.message || "Failed to load ticket",
          });
          this.$router.push("/raise-tickets");
        }
      } catch (error) {
        console.error("Error loading raise ticket:", error);
        if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Error loading raise ticket",
          });
          this.$router.push("/raise-tickets");
        }
      } finally {
        this.isLoading = false;
      }
    },

    async getMessages() {
      try {
        this.isLoadingMessages = true;
        const response = await axios.get(
          `/raise_ticket_messages/ticket/${this.ticket.id}`
        );
        this.messages = response.data.data || [];
        this.$nextTick(() => this.scrollToBottom());

        await axios.patch(`/raise_ticket_messages/mark-all-seen`, {
          ticket_id: this.ticket.id,
        });
      } catch (error) {
        console.error("Error loading ticket messages:", error);
      } finally {
        this.isLoadingMessages = false;
      }
    },

    async updateStatus() {
      try {
        this.isUpdatingStatus = true;
        const encryptedId = this.$route.params.id;
        const response = await axios.patch(`/raise_tickets/${encryptedId}`, {
          status: this.selectedStatus,
        });

        if (response.data.success) {
          this.ticket = { ...this.ticket, ...response.data.data };
          this.$toast.success("Ticket status updated successfully");
        }
      } catch (error) {
        console.error("Error updating ticket status:", error);
        this.$store.dispatch("errors/setError", {
          message: "Failed to update ticket status",
        });
      } finally {
        this.isUpdatingStatus = false;
      }
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        if (file.size > 10 * 1024 * 1024) {
          this.$store.dispatch("errors/setError", {
            message: "File size cannot exceed 10MB",
          });
          return;
        }
        this.selectedFile = file;
      }
    },

    async sendMessage() {
      if (!this.newMessage.trim() && !this.selectedFile) return;

      const formData = new FormData();
      formData.append("raise_ticket_id", this.ticket.id);
      if (this.newMessage.trim()) {
        formData.append("message", this.newMessage.trim());
      }
      if (this.selectedFile) {
        formData.append("file", this.selectedFile);
      }

      try {
        this.isSending = true;
        const response = await axios.post(
          "/raise_ticket_messages",
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        if (response.data.success) {
          this.messages.push(response.data.data);
          this.newMessage = "";
          this.selectedFile = null;
          this.$nextTick(() => this.scrollToBottom());
        }
      } catch (error) {
        console.error("Error sending message:", error);
        this.$store.dispatch("errors/setError", {
          message: "Failed to send message",
        });
      } finally {
        this.isSending = false;
      }
    },

    scrollToBottom() {
      const el = this.$refs.messagesContainer;
      if (el) el.scrollTop = el.scrollHeight;
    },

    isMine(message) {
      return message.sender_id === this.user.id;
    },

    getUserName(personOrObj) {
      if (!personOrObj) return "N/A";
      const name = [personOrObj.first_name, personOrObj.last_name]
        .filter(Boolean)
        .join(" ");
      return name || personOrObj.user_name || "N/A";
    },

    getStatusLabel(status) {
      const found = this.statuses.find((s) => s.value === status);
      return found ? found.text : "Unknown";
    },

    getStatusColor(status) {
      switch (status) {
        case 0:
          return "orange";
        case 1:
          return "blue";
        case 2:
          return "green";
        case 3:
          return "red";
        default:
          return "grey";
      }
    },

    getStatusIcon(status) {
      switch (status) {
        case 0:
          return "mdi-clock-outline";
        case 1:
          return "mdi-progress-clock";
        case 2:
          return "mdi-check-circle";
        case 3:
          return "mdi-close-circle";
        default:
          return "mdi-help-circle";
      }
    },

    formatDate(dateString) {
      if (!dateString) return "";
      return new Date(dateString).toLocaleString();
    },

    formatTime(dateString) {
      if (!dateString) return "";
      return new Date(dateString).toLocaleString();
    },
  },
};
</script>

<style scoped>
.rt-msgs {
  max-height: 380px;
  overflow-y: auto;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
}

.rt-msg-row {
  display: flex;
  margin-bottom: 10px;
}

.rt-msg-row--mine {
  justify-content: flex-end;
}

.rt-msg-bubble {
  max-width: 70%;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  padding: 8px 12px;
}

.rt-msg-bubble--mine {
  background: #e3f2fd;
  border-color: #90caf9;
}

.rt-msg-sender {
  font-size: 0.7rem;
  font-weight: 600;
  color: #1976d2;
  margin-bottom: 2px;
}

.rt-msg-text {
  font-size: 0.875rem;
  white-space: pre-line;
}

.rt-msg-file {
  display: inline-flex;
  align-items: center;
  font-size: 0.8rem;
  margin-top: 4px;
}

.rt-msg-time {
  font-size: 0.65rem;
  color: #9e9e9e;
  margin-top: 4px;
  text-align: right;
}
</style>
